import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Loader2, MessageSquarePlus, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["company", "center", "department", "person"];
const TYPES = [{ v: "numeric", l: "Numeric target" }, { v: "percentage", l: "Percentage" }, { v: "milestone", l: "Milestone (yes/no)" }, { v: "checklist", l: "Checklist" }];

export default function Goals() {
  const { effectiveRole } = useAuth();
  const canWrite = effectiveRole !== "viewer";
  const [goals, setGoals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [ciOpen, setCiOpen] = useState(false);
  const [ciGoal, setCiGoal] = useState(null);
  const [ci, setCi] = useState({ comment: "", confidence: 7 });

  const load = async () => { const { data } = await api.get("/goals"); setGoals(data); };
  useEffect(() => { load(); }, []);

  const roots = goals.filter((g) => !g.parent_id);
  const childrenOf = (id) => goals.filter((g) => g.parent_id === id);

  const start = () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const end = new Date(new Date().setMonth(11, 31)).toISOString().slice(0, 10);
    setForm({ title: "", level: "company", type: "numeric", start_date: todayStr, end_date: end, target: 100, current: 0, unit: "", weight: 1, parent_id: "" });
    setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, target: Number(form.target), current: Number(form.current), weight: Number(form.weight), parent_id: form.parent_id || null };
      await api.post("/goals", payload);
      toast.success("Goal created"); setOpen(false); setTimeout(load, 250);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (g) => {
    try { await api.delete(`/goals/${g.id}`); toast.success("Goal deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const submitCheckin = async () => {
    try { await api.post(`/goals/${ciGoal.id}/checkin`, ci); toast.success("Check-in logged"); setCiOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const Row = ({ g, depth }) => (
    <div className="rounded-xl border border-border p-4 hover:shadow-sm transition-shadow" style={{ marginLeft: depth * 20 }} data-testid={`goal-${g.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {depth > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
            <p className="font-semibold text-sm">{g.title}</p>
            <Badge variant="outline" className="capitalize text-[10px]">{g.level}</Badge>
            <StatusBadge status={g.status} />
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{g.owner} · {g.type}{g.unit ? ` · ${g.unit}` : ""}</p>
          <div className="flex items-center gap-3 mt-2">
            <Progress value={g.progress} className="h-2 flex-1" />
            <span className="text-xs font-semibold tabular-nums w-20 text-right">{g.progress}% <span className="text-muted-foreground font-normal">/ {g.expected}% exp</span></span>
          </div>
        </div>
        <div className="flex shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setCiGoal(g); setCi({ comment: "", confidence: 7 }); setCiOpen(true); }} data-testid={`checkin-goal-${g.id}`}><MessageSquarePlus className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => remove(g)} data-testid={`delete-goal-${g.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </div>
  );

  const flat = [];
  const walk = (g, depth) => { flat.push({ g, depth }); childrenOf(g.id).forEach((k) => walk(k, depth + 1)); };
  roots.forEach((r) => walk(r, 0));

  return (
    <div data-testid="goals-page">
      <PageHeader eyebrow="Strategy" title="Goals & OKR Engine"
        subtitle="Company → Center → Department → Person. Progress rolls up; status auto-calculates vs. elapsed time."
        action={<Button onClick={start} data-testid="add-goal-button" style={{ display: canWrite ? undefined : "none" }}><Plus className="h-4 w-4 mr-1" /> New Goal</Button>} />

      <div className="space-y-3">
        {flat.map(({ g, depth }) => <Row key={g.id} g={g} depth={depth} />)}
        {roots.length === 0 && <Card className="p-10 text-center text-muted-foreground text-sm">No goals yet. Create your first objective.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="goal-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>New Goal</DialogTitle><DialogDescription>Define an objective or key result at any level.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="goal-title-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Owner</Label><Input value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Level</Label>
                <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger data-testid="goal-level-select"><SelectValue /></SelectTrigger><SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="goal-type-select"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
            </div>
            {(form.type === "numeric" || form.type === "percentage") && (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">Target</Label><Input type="number" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Current</Label><Input type="number" value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Unit</Label><Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Weight</Label><Input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Parent goal</Label>
                <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None (top-level)</SelectItem>{goals.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.title} data-testid="save-goal-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ciOpen} onOpenChange={setCiOpen}>
        <DialogContent data-testid="goal-checkin-dialog">
          <DialogHeader><DialogTitle>Weekly check-in</DialogTitle><DialogDescription>{ciGoal?.title}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Comment</Label><Textarea value={ci.comment} onChange={(e) => setCi({ ...ci, comment: e.target.value })} data-testid="checkin-comment-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Confidence (1-10): {ci.confidence}</Label>
              <Input type="range" min="1" max="10" value={ci.confidence} onChange={(e) => setCi({ ...ci, confidence: Number(e.target.value) })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCiOpen(false)}>Cancel</Button>
            <Button onClick={submitCheckin} data-testid="save-goal-checkin-button">Log check-in</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
